/**
 * Shared types and context for chat runtime providers.
 *
 * This module provides the common types and context used by CanvasChatRuntimeProvider.
 * The actual provider implementation lives in CanvasChatRuntimeProvider.tsx.
 *
 * ## Future Thread Support
 * When implementing thread persistence, see the deleted `SupabaseThreadProvider.tsx`
 * in git history (removed in BRICK-326) as a reference implementation. It handled:
 * - Thread list management with Supabase
 * - Message persistence and hydration
 * - Conversation state synchronization
 *
 * To find it: `git log --all --full-history -- src/chat/SupabaseThreadProvider.tsx`
 */

import { createContext } from "react";
import type { Enums } from "@/types/supabase";

export type AgentPlanStep = {
  goal: string;
  tool?: string | null;
  inputs?: Record<string, unknown>;
  roundIndex: number;
  position: number;
};

export type AgentPlanRound = {
  round: number;
  steps: AgentPlanStep[];
};

export type AgentPlanState = {
  iteration: number;
  totalIterations?: number;
  steps: AgentPlanStep[];
  rounds: AgentPlanRound[];
};

export type AgentProgressStatus = "pending" | "running" | "success" | "failed";

export type AgentProgressEntry = {
  iteration: number;
  stepIndex: number;
  step: string;
  tool: string;
  status: AgentProgressStatus;
  roundIndex: number;
  preview?: unknown;
};

export type AgentActivityStatus = AgentProgressStatus | AgentWorkflowStage;

export type AgentActivity = {
  key: string;
  label: string;
  status: AgentActivityStatus;
  preview?: unknown;
};

export type AgentToolCall = {
  investigationId: string;
  investigationQuestion?: string;
  tool: string;
  step: number;
  nodeId?: string;
  status: AgentProgressStatus;
  preview?: string;
  error?: string;
  timestamp: number;
};

export type AgentInvestigation = {
  id: string;
  question: string;
  status: AgentProgressStatus;
  iteration: number;
  toolCalls: AgentToolCall[];
};

export type AgentWorkflowStage = "planning" | "answering" | "investigating" | "done";

export type AgentTimelineEntry = {
  plan: AgentPlanState;
  progress: Record<number, AgentProgressEntry>;
  status: AgentWorkflowStage;
};

export type AgentEventsState = {
  status?: AgentWorkflowStage;
  plan?: AgentPlanState;
  activeIteration?: number;
  timeline: AgentTimelineEntry[];
  progress: Record<string, AgentProgressEntry>;
  currentActivity?: AgentActivity;
  toolCalls: Record<string, AgentToolCall[]>;
  investigations: Record<number, AgentInvestigation[]>;
  confirmationMessage?: string | null;
  estimationMessage?: string | null;
};

export type ChatRuntimeContextValue = {
  convoType: Enums<"convo_type">;
  projectId?: string;
  organizationId?: string;
  /** Set when in AI Document (canvas) chat context. Presence indicates canvas-specific tracking is handled by the provider. */
  documentId?: string;
  agentEvents: AgentEventsState;
  resetAgentEvents: () => void;
};

// eslint-disable-next-line react-refresh/only-export-components
export const ChatRuntimeContext = createContext<ChatRuntimeContextValue | null>(null);
